// components/LatestPosts.tsx
"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import SafeHtml from "@/components/ui/SafeHtml";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import { fetchPostsGrid } from "@/lib/fetchPostsGrid";
import { getLocaleFromLang } from "@/lib/getLocaleFromLang";
import type { LatestPostsFields } from "@/types/acf";
import type { WPPost } from "@/types/wpBase";

function formatDate(date?: string, lang?: string) {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(getLocaleFromLang(lang), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function getWpFeaturedImage(post: unknown): { src: string; alt: string } | null {
  if (!isRecord(post)) return null;
  if (!post["featured_media"]) return null;

  const embedded = post["_embedded"];
  if (!isRecord(embedded)) return null;
  const featuredMedia = embedded["wp:featuredmedia"];
  if (!Array.isArray(featuredMedia) || featuredMedia.length === 0) return null;

  const fm = featuredMedia[0];

  if (!isRecord(fm)) return null;

  const mediaDetails = fm["media_details"];
  const sizes = isRecord(mediaDetails) ? mediaDetails["sizes"] : undefined;

  const large = isRecord(sizes) ? sizes["large"] : undefined;
  const mediumLarge = isRecord(sizes) ? sizes["medium_large"] : undefined;

  const largeUrl =
    isRecord(large) && typeof large["source_url"] === "string" ? large["source_url"] : undefined;

  const mediumLargeUrl =
    isRecord(mediumLarge) && typeof mediumLarge["source_url"] === "string"
      ? mediumLarge["source_url"]
      : undefined;

  const sourceUrl = typeof fm["source_url"] === "string" ? fm["source_url"] : undefined;

  const url = largeUrl || mediumLargeUrl || sourceUrl;

  const abs = url ? toAbsoluteUrl(url) : null;
  if (!abs) return null;

  const alt = typeof fm["alt_text"] === "string" ? fm["alt_text"] : "";

  return { src: abs, alt };
}

export default function LatestPosts({ data, lang }: { data: LatestPostsFields; lang?: string }) {
  const isHidden = !data || !!data.hide;
  const langCode = lang;
  const [posts, setPosts] = React.useState<WPPost[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (isHidden) return;

    let alive = true;
    setIsLoading(true);

    fetchPostsGrid(1, 3, langCode)
      .then((r) => {
        if (!alive) return;
        setPosts(r.posts || []);
      })
      .finally(() => {
        if (!alive) return;
        setIsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [langCode, isHidden]);

  if (isHidden) return null;

  return (
    <div id={data.id}>
      {data.title ? (
        <h2 className="text-title-2 text-center mb-6"> 
          <SafeHtml content={data.title} />
        </h2>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={`latest-skel-${i}`} className="w-full">
                <div className="relative w-full aspect-[3/2] overflow-hidden">
                  <div className="absolute inset-0 animate-pulse bg-black/10" />
                </div>
                <div className="mt-3">
                  <div className="h-4 w-36 animate-pulse bg-black/10" />
                  <div className="h-5 w-5/6 mt-2 animate-pulse bg-black/10" />
                </div>
              </div>
            ))
          : posts.map((post) => {
              const href = post.link ?? "#";
              const image = getWpFeaturedImage(post);
              const date = formatDate(post.date, langCode);
              const title = post.title?.rendered || "";

              return (
                <Link
                  key={post.id}
                  href={href}
                  className="group block w-full no-underline hover:no-underline"
                >
                  {image?.src ? (
                    <div className="relative w-full aspect-[3/2] overflow-hidden">
                      <div className="absolute inset-0 bg-black/10" aria-hidden="true" />
                      <div className="absolute inset-[-2px] transition-transform duration-200 ease-out will-change-transform group-hover:scale-[1.025]">
                        <Image
                          src={image.src}
                          alt={image.alt}
                          fill
                          className="object-cover"
                          sizes="(min-width: 768px) 33vw, 100vw"
                          priority={false}
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-3">
                    {date ? <div className="font-regular">{date}</div> : null}
                    {title ? (
                      <h3 className="mt-1 text-title-4">
                        <SafeHtml content={title} />
                      </h3>
                    ) : null}
                  </div>
                </Link>
              );
            })}
      </div>
    </div>
  );
}
